/**
 * Shared row actions for a management-fee invoice.
 *
 * One implementation used by every surface that lists owner invoices — the
 * Payments hub (/admin?tab=payments&sub=management) and the Owners → Invoices
 * tab — so "view invoice / upload proof / note / mark paid" behave identically
 * wherever an admin happens to be standing.
 *
 * Usage:
 *   const { renderActions, overlays } = useManagementInvoiceActions();
 *   …<TableCell>{renderActions(row)}</TableCell>…
 *   {overlays}   // hidden file input + preview/note/delete dialogs
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { logOwnerAudit } from "@/lib/owner-audit";
import { OWNER_AUDIT_KEY } from "@/hooks/v2/useOwnerDetail";
import { invalidateOwnerCaches } from "@/hooks/v2/owner-detail-keys";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  isOffsetLiveFeeEditError,
  sendInvoiceErrorCode,
} from "@/lib/offset-live-fee";
import {
  isLiveOffsetInvoice,
  isOffsetPropertyForecast,
  isRemainderReopenRefusal,
  unpaidOffsetSiblings,
} from "@/lib/offset-remainder";
import {
  fetchOpenRemainderNo,
  notifyRemainderOpened,
} from "@/hooks/v2/offset-remainder-queries";
import { offsetPayoutNote } from "@/lib/management-invoice-lines";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Upload,
  Check,
  Undo2,
  Eye,
  Loader2,
  Pencil,
  Trash2,
  Send,
  StickyNote,
  AlertTriangle,
  FileX,
  Paperclip,
  ArrowLeftRight,
  MoreHorizontal,
  Settings2,
} from "lucide-react";
import { PaymentConfigurationAnchor } from "./PaymentConfigurationAnchor";
import { paymentConfigurationPath } from "@/lib/payment-configuration-link";
import {
  buildManagementInvoiceData,
  effectiveDueDate,
  managementInvoiceFieldValues,
  MGMT_VAT_RATE,
} from "@/lib/invoice";
import type { InvoiceBank, InvoiceData } from "@/lib/invoice";
import { stampInvoicePdf } from "@/lib/invoice-stamp";
import { splitGrossVat } from "@/lib/management-invoice-lines";
import { formatCents } from "@/lib/money";
import type { PlacedField } from "@/components/admin/TemplateFieldEditor";
import { downloadStoredFile, openStoredFile } from "@/lib/storage-url";
import {
  useManagementInvoiceMutations,
  MANAGEMENT_INVOICE_KEYS,
  MARK_PAID_CANCELED,
  UNMARK_PAID_NOT_PAID,
  type ManagementInvoiceRow,
} from "@/hooks/v2/useManagementInvoices";
import {
  canCancelAndReissueManagementInvoice,
  canEditManagementInvoice,
  isOffsetInvoice,
} from "@/types/billing";
import { InvoicePreviewDialog } from "./InvoicePreviewDialog";
import {
  usePlatformBankAccounts,
  usePlatformLessor,
  resolvePlatformAccount,
} from "@/hooks/v2/useProperties";
import type { Property } from "@/types/property";

/**
 * The properties an invoice actually covers: its own property_id for a legacy
 * per-property row, else the fee_basis breakdown a CONSOLIDATED per-owner row
 * carries. Used to point the owner's name at an apartment inside the property
 * being billed, rather than at any apartment they happen to hold.
 */
export function invoicePropertyIds(row: {
  property_id?: number | null;
  fee_basis?: Record<string, unknown> | null;
}): number[] {
  if (row.property_id != null) return [Number(row.property_id)];
  const props = Array.isArray(row.fee_basis?.properties)
    ? (row.fee_basis!.properties as Array<{ property_id?: number | string }>)
    : [];
  return props
    .map((p) => Number(p.property_id))
    .filter((n) => Number.isFinite(n));
}

/**
 * What a management-invoice row is FOR, as a label.
 *
 * Since 20260904120000 an invoice bills one apartment, so that is the name —
 * read from fee_basis.properties[0].apartment_label, the same qualified string
 * ("Whg. 1 – Haus A") the PDF prints. This is checked BEFORE the property join
 * on purpose: a per-apartment invoice sets property_id (so the payable bank
 * resolves), so preferring the join would label every flat in a building with
 * the building's name and make four of an owner's invoices indistinguishable.
 *
 * Older rows keep their old labels: a legacy per-property invoice joins its
 * property (title/city); a legacy CONSOLIDATED invoice has property_id NULL and
 * no join, so it shows the property count (or the single title) with the full
 * list as a tooltip.
 */
export function invoicePropertyLabel(
  row: {
    properties?: { title?: string | null; city?: string | null } | null;
    fee_basis?: Record<string, unknown> | null;
  },
  t: (key: string, fallback: string) => string = (_key, fallback) =>
    fallback ?? ""
): { title: string; city: string | null; full: string | null } {
  const unit = Array.isArray(row.fee_basis?.properties)
    ? (row.fee_basis!.properties as Array<{ apartment_label?: string }>)
    : [];
  if (unit.length === 1 && unit[0]?.apartment_label)
    return {
      title: String(unit[0].apartment_label),
      city: row.properties?.city ?? null,
      full: null,
    };
  if (row.properties?.title)
    return {
      title: row.properties.title,
      city: row.properties.city ?? null,
      full: null,
    };
  const props = Array.isArray(row.fee_basis?.properties)
    ? (row.fee_basis!.properties as Array<{ property_title?: string }>)
    : [];
  const titles = props.map((p) => p.property_title).filter(Boolean) as string[];
  if (titles.length === 0) return { title: "—", city: null, full: null };
  if (titles.length === 1) return { title: titles[0], city: null, full: null };
  return {
    title: t(
      "admin.managementInvoiceActions.propertiesCount",
      "{count} properties"
    ).replace("{count}", String(titles.length)),
    city: null,
    full: titles.join(", "),
  };
}

export function useManagementInvoiceActions(): {
  /**
   * `compact` drops the button labels to icons (keeping them as tooltips) so
   * the cluster fits a narrow table — e.g. the owner modal's invoice history,
   * where labelled buttons pushed the Action column off-screen.
   */
  renderActions: (
    row: ManagementInvoiceRow,
    opts?: {
      compact?: boolean;
      ownerStyle?: boolean;
      /**
       * The net payout, in cents, that confirming this invoice's payout will
       * record (COL-516). Given only by a surface that has the live figure (the
       * owner profile's schedule); without it the dialog shows no amount and the
       * confirmation records none.
       */
      netPayoutCents?: number | null;
    }
  ) => ReactNode;
  overlays: ReactNode;
} {
  const qc = useQueryClient();
  const { t, language } = useLanguage();
  // Marking an invoice paid / confirming an offset settlement is a
  // super-admin-only action (COL-461) — enforced here (hide the control) AND
  // server-side by enforce_management_invoice_paid_gate, since hiding a
  // button is not itself a permission boundary.
  const { isSuperAdmin } = useAuth();
  const { data: platformAccounts = [] } = usePlatformBankAccounts();
  const { data: platformLessor } = usePlatformLessor();
  const {
    uploadProof,
    markPaid,
    unmarkPaid,
    deleteProof,
    addProofNote,
    confirmOffset,
    createInvoice,
    generatePropertyMonth,
    editInvoice,
    cancelAndReissue,
  } = useManagementInvoiceMutations();

  // Every invoice action is available from BOTH the Payments hub and the owner
  // modal, so the trail is written here rather than per surface — and the
  // owner's feed is refreshed even when the action was taken from the hub.
  const invalidateOwnerAudit = (row: ManagementInvoiceRow) =>
    qc.invalidateQueries({ queryKey: OWNER_AUDIT_KEY(row.lessor_id ?? null) });

  const fileRef = useRef<HTMLInputElement>(null);
  // The invoice a proof is being uploaded for. Held as the full row (not just
  // an id) so the mandatory-note modal can open on it right after the upload.
  const [target, setTarget] = useState<ManagementInvoiceRow | null>(null);
  // Mandatory-note modal (opens right after a proof uploads) and the
  // mandatory-reason modal for deleting a proof.
  const [noteFor, setNoteFor] = useState<ManagementInvoiceRow | null>(null);
  // "Record payout" dialog: settling a company_collects invoice against
  // the rent CoLiviq collected. Separate from the proof-note dialog because it settles
  // the invoice in the same write (the DB gate reads the note off the row being
  // marked paid), where a proof note only prepares one for a later Mark paid.
  const [offsetFor, setOffsetFor] = useState<ManagementInvoiceRow | null>(null);
  const [offsetNote, setOffsetNote] = useState("");
  // The net payout the open dialog shows and the confirmation will record
  // (COL-516). Null when the surface that opened it had no live figure.
  const [offsetNetPayout, setOffsetNetPayout] = useState<number | null>(null);
  // COL-483: after an early send, the sent invoice and its remainder can both
  // be waiting for a payout, and one transfer may cover both — name the other.
  const offsetSiblings = offsetFor
    ? unpaidOffsetSiblings(
        offsetFor,
        qc.getQueryData<ManagementInvoiceRow[]>(
          MANAGEMENT_INVOICE_KEYS.list()
        ) ?? []
      )
    : [];
  const [noteDraft, setNoteDraft] = useState("");
  const [deleteFor, setDeleteFor] = useState<ManagementInvoiceRow | null>(null);
  const [reasonDraft, setReasonDraft] = useState("");
  // Cancel + reissue (Storno) for an invoice the owner already holds. Carries
  // its own reason/amount/due drafts rather than sharing the edit dialog's:
  // this is a different operation with a mandatory reason, and reusing the
  // edit state would let a half-typed edit leak into a cancellation.
  const [reissueFor, setReissueFor] = useState<ManagementInvoiceRow | null>(
    null
  );
  const [reissueReason, setReissueReason] = useState("");
  const [reissueAmount, setReissueAmount] = useState("");
  const [reissueDue, setReissueDue] = useState("");
  // Amount/due-date edit dialog. Works on a real row (update in place) or a
  // projected placeholder (creates the real invoice with the edited values).
  const [editFor, setEditFor] = useState<ManagementInvoiceRow | null>(null);
  const [editAmount, setEditAmount] = useState("");
  const [editDue, setEditDue] = useState("");
  // What to do with the row once the dialog succeeds — set when the dialog is
  // only the confirmation step in front of uploading a proof / adding a note.
  const [editThen, setEditThen] = useState<"proof" | "note" | null>(null);
  // That follow-up, queued until the create dialog has actually closed: a file
  // picker or a second dialog opened while Radix still owns focus is flaky.
  const [afterCreate, setAfterCreate] = useState<{
    kind: "proof" | "note";
    row: ManagementInvoiceRow;
  } | null>(null);
  const [preview, setPreview] = useState<{
    data: InvoiceData;
    row: ManagementInvoiceRow;
  } | null>(null);
  const [pdfPreview, setPdfPreview] = useState<{
    url: string;
    row: ManagementInvoiceRow;
  } | null>(null);
  const [stamping, setStamping] = useState(false);
  // Track the live blob URL so we can revoke it on unmount too (closePdfPreview
  // only fires on an explicit close).
  const pdfUrlRef = useRef<string | null>(null);

  function closePdfPreview() {
    setPdfPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });
    pdfUrlRef.current = null;
  }

  useEffect(
    () => () => {
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
    },
    []
  );

  // Resolve the CoLiviq "pay to" account for an invoice: the property's platform
  // (CoLiviq-held) bank account → the CoLiviq lessor's default → the default
  // platform account. Returns undefined when nothing resolves (Send stays gated).
  //
  // An OFFSET invoice needs none: it prints no Bankverbindung, because the fee
  // was deducted from the rent CoLiviq collected. Returning undefined for it is
  // therefore not a missing-account problem, so `canSendInvoice` below is what
  // gates Send — not the presence of a bank alone.
  const platformBankFor = (
    row: ManagementInvoiceRow
  ): InvoiceBank | undefined => {
    if (isOffsetInvoice(row)) return undefined;
    const acct = resolvePlatformAccount({
      property: {
        platform_bank_account_id:
          row.properties?.platform_bank_account_id ?? null,
      } as Property,
      accounts: platformAccounts,
      coliviqLessor: platformLessor ?? null,
    });
    if (!acct?.iban) return undefined;
    return {
      recipient: acct.holder,
      iban: acct.iban,
      bic: acct.bic,
      bankName: acct.bank_name,
    };
  };

  /**
   * Whether this invoice can be emailed to the owner.
   *
   * A payable invoice tells the owner where to pay, so one with no resolvable
   * CoLiviq account is unpayable and must not go out. An offset invoice asks
   * for no payment and prints no account, so the same gate would block a
   * document that is already settled. Mirrors the server-side check in
   * send-invoice, which is the actual control.
   */
  const canSendInvoice = (row: ManagementInvoiceRow): boolean =>
    isOffsetInvoice(row) || !!platformBankFor(row);

  // A send can open a remainder (COL-483) and freezes a live fee, and the
  // owners list's payouts view derives from these invoices too. That list
  // stays mounted under the owner modal a send is often made from, so refresh
  // every owner cache with the invoice list, as the other invoice writes do.
  const refreshAfterSend = (row: ManagementInvoiceRow) => {
    qc.invalidateQueries({ queryKey: MANAGEMENT_INVOICE_KEYS.list() });
    qc.invalidateQueries({ queryKey: MANAGEMENT_INVOICE_KEYS.analytics() });
    invalidateOwnerCaches(qc, row.lessor_id ?? null);
  };

  const sendInvoice = useMutation({
    mutationFn: async (row: ManagementInvoiceRow) => {
      const { data, error } = await supabase.functions.invoke("send-invoice", {
        body: { kind: "management", invoiceId: row.id },
      });
      // COL-474: send-invoice's refusals carry a stable code — show each as
      // translated copy, not the generic non-2xx error.
      const code = error ? await sendInvoiceErrorCode(error) : null;
      const refusal: Record<string, string> = {
        offset_fee_zero: t(
          "admin.payments.offsetZeroFeeSendRefused",
          "This offset invoice's fee is still 0,00 € on the rent collected so far. Sending would freeze it at zero — send it once the fee is above zero, or after the payout."
        ),
        send_lock_failed: t(
          "admin.payments.sendLockFailed",
          "The send couldn't be recorded, so nothing was emailed. Please try again."
        ),
        send_already_sent: t(
          "admin.payments.sendAlreadySent",
          "This invoice was just sent from another window."
        ),
        send_amount_changed: t(
          "admin.payments.sendAmountChanged",
          "The fee changed while this invoice was being sent, so nothing was emailed. Please send it again."
        ),
        send_not_confirmed: t(
          "admin.payments.sendNotConfirmed",
          "The invoice is marked as sent and its amount is locked, but the email may not have reached the owner. If they didn't get it, send it again — the amount won't change."
        ),
        send_canceled: t(
          "admin.payments.sendCanceledRefused",
          "This invoice has been canceled, so it can't be sent."
        ),
      };
      if (code && refusal[code]) throw new Error(refusal[code]);
      if (error) throw error;
      // The amount the send actually emailed (and froze). A live offset fee
      // may have moved since the preview was opened, as more rent came in —
      // the audit row records what the owner received, not the preview.
      const reply = (data ?? {}) as {
        amount_cents?: unknown;
        remainder_invoice_no?: unknown;
      };
      const sentCents =
        typeof reply.amount_cents === "number"
          ? reply.amount_cents
          : row.amount_cents;
      // COL-483: sent while rent was still coming in, it opened a remainder.
      const remainderNo =
        typeof reply.remainder_invoice_no === "string"
          ? reply.remainder_invoice_no
          : null;
      // sent_at is stamped by the edge function; just refresh to pick it up.
      //
      // The stamp is also why this log exists. `sent_at` holds ONE value, so
      // re-sending an invoice overwrites it and the owner's Activity tab lost
      // the earlier send — the row moved instead of a second row appearing.
      // Every send writes its own audit row, so the history keeps them all.
      await logOwnerAudit({
        lessorId: row.lessor_id,
        action: "management_invoice_sent",
        resourceType: "management_invoice",
        resourceId: String(row.id),
        newValues: {
          invoice_no: row.invoice_no,
          amount_cents: sentCents,
        },
        metadata: { recipient: row.lessors?.email ?? null },
      });
      return { sentCents, remainderNo };
    },
    onSuccess: (res, row) => {
      toast.success(
        t("admin.payments.toastInvoiceSent", "Invoice sent to owner.")
      );
      if (res.sentCents !== row.amount_cents) {
        toast.warning(
          t(
            "admin.payments.toastSentAmountMoved",
            "Sent at {sent}: the fee had moved from {previewed} since you opened it, as more rent came in."
          )
            .replace("{sent}", formatCents(res.sentCents))
            .replace("{previewed}", formatCents(row.amount_cents))
        );
      }
      if (res.remainderNo) notifyRemainderOpened(res.remainderNo, t);
      refreshAfterSend(row);
    },
    onError: (e: any, row) => {
      toast.error(
        e?.message ??
          t("admin.payments.toastSendFailed", "Could not send invoice")
      );
      // A failed send can still have changed the row: an unconfirmed send is
      // marked sent (and may have opened a remainder), and a held live fee
      // may have been recomputed (COL-474).
      refreshAfterSend(row);
    },
  });

  // Stable: the afterCreate effect below depends on these two.
  const pickProof = useCallback((row: ManagementInvoiceRow) => {
    setTarget(row);
    fileRef.current?.click();
  }, []);
  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !target) return;
    // Snapshot before the mutation: uploadProof clears any prior note from this
    // metadata, and we reuse the row to open the mandatory-note modal.
    const row = target;
    try {
      await uploadProof.mutateAsync({
        id: row.id,
        file,
        metadata: row.metadata,
      });
      await logOwnerAudit({
        lessorId: row.lessor_id,
        action: "management_invoice_proof_uploaded",
        resourceType: "management_invoice",
        resourceId: String(row.id),
        newValues: { file_name: file.name },
        metadata: { file_name: file.name },
      });
      invalidateOwnerAudit(row);
      toast.success(
        t(
          "admin.payments.toastMgmtProofUploaded",
          "Proof uploaded — add a note to continue."
        )
      );
      // A note is mandatory before the invoice can be marked paid: open the
      // note modal on the just-uploaded invoice. The prior note was just cleared
      // by uploadProof, so start the draft empty.
      setNoteDraft("");
      setNoteFor(row);
    } catch (err: any) {
      toast.error(
        err?.message ?? t("admin.payments.toastUploadFailed", "Upload failed")
      );
    }
  };
  const openNote = useCallback((row: ManagementInvoiceRow) => {
    setNoteDraft(
      String((row.metadata as Record<string, unknown>)?.proof_note ?? "")
    );
    setNoteFor(row);
  }, []);
  const submitNote = async () => {
    if (!noteFor || !noteDraft.trim()) return;
    try {
      await addProofNote.mutateAsync({
        id: noteFor.id,
        note: noteDraft.trim(),
        metadata: noteFor.metadata ?? {},
      });
      // metadata.proof_note holds one note: editing it replaced the previous
      // one in the feed. Each note now also lands here as its own entry.
      await logOwnerAudit({
        lessorId: noteFor.lessor_id,
        action: "management_invoice_note_added",
        resourceType: "management_invoice",
        resourceId: String(noteFor.id),
        metadata: { note: noteDraft.trim() },
      });
      invalidateOwnerAudit(noteFor);
      toast.success(
        t(
          "admin.payments.toastNoteSaved",
          "Note saved — you can now mark this invoice paid."
        )
      );
      setNoteFor(null);
      setNoteDraft("");
    } catch (err: any) {
      toast.error(
        err?.message ??
          t("admin.payments.toastNoteFailed", "Could not save note")
      );
    }
  };
  /**
   * Open the confirm-offset dialog with a note already drafted.
   *
   * Pre-filled rather than blank because the note is the ONLY record that this
   * fee was settled — an empty box invites "ok" as the audit trail. The draft
   * names the invoice and its period, which is what a later reconciliation
   * needs; the admin can still rewrite it.
   */
  const openOffsetConfirm = useCallback(
    (row: ManagementInvoiceRow, netPayoutCents: number | null = null) => {
      setOffsetNote(offsetPayoutNote(row));
      setOffsetNetPayout(netPayoutCents);
      setOffsetFor(row);
    },
    []
  );

  const submitOffsetConfirm = async () => {
    if (!offsetFor || !offsetNote.trim()) return;
    const row = offsetFor;
    const note = offsetNote.trim();
    try {
      await confirmOffset.mutateAsync({
        row,
        note,
        netPayoutCents: offsetNetPayout,
      });
    } catch (err: any) {
      toast.error(err?.message ?? "Could not settle this invoice");
      return;
    }
    // The DB write already succeeded at this point — an audit-log failure past
    // here must not be reported as "could not settle", or an admin who sees
    // that error retries an already-completed confirmation, and the retry then
    // fails for real (confirmOffset's own WHERE guard refuses an already-paid
    // row), reporting a settled invoice as voided/unsettleable. Same
    // fire-and-forget shape as markPaid/unmarkPaid's own audit call.
    void logOwnerAudit({
      lessorId: row.lessor_id,
      action: "management_invoice_offset_confirmed",
      resourceType: "management_invoice",
      resourceId: String(row.id),
      metadata: { note },
    })
      .then(() => invalidateOwnerAudit(row))
      .catch((e) => console.error("submitOffsetConfirm: audit log failed", e));
    toast.success(
      t(
        "admin.payments.toastNetRentTransferred",
        "Net rent transferred — the fee is settled against the rent collected."
      )
    );
    // Only a live invoice's close opens a remainder (one sent before its
    // payout opened it at the send).
    if (isLiveOffsetInvoice(row)) {
      void fetchOpenRemainderNo(row.id).then((no) => {
        if (no) notifyRemainderOpened(no, t);
      });
    }
    setOffsetFor(null);
    setOffsetNote("");
    setOffsetNetPayout(null);
  };

  // Drain a follow-up queued behind the create dialog, once that dialog has
  // closed. Re-runs are harmless: the queue is cleared as it fires.
  useEffect(() => {
    if (!afterCreate || editFor) return;
    if (afterCreate.kind === "proof") pickProof(afterCreate.row);
    else openNote(afterCreate.row);
    setAfterCreate(null);
  }, [afterCreate, editFor, pickProof, openNote]);

  /**
   * Open the amount/due-date dialog. `then` is the action to run on the row
   * once the dialog succeeds — for a PROJECTED row that row does not exist yet,
   * so uploading a proof or adding a note has to go through here first: the
   * amount an invoice is created with is always one the admin confirmed, never
   * the anchor month's figure carried in silently.
   */
  const openEdit = (
    row: ManagementInvoiceRow,
    then: "proof" | "note" | null = null
  ) => {
    setEditAmount(((row.amount_cents || 0) / 100).toString());
    // A projected placeholder has no due_date yet — default it the same way
    // the generator would (today + MGMT_DUE_DAYS), so a manually created
    // invoice doesn't diverge from a generated one on this. A real row keeps
    // whatever due_date it actually has (including none).
    setEditDue(
      row.due_date
        ? String(row.due_date).slice(0, 10)
        : row.__projected
          ? (effectiveDueDate({ issue_date: null, due_date: null }) ?? "")
          : ""
    );
    setEditThen(then);
    setEditFor(row);
  };

  /**
   * Create the real invoice behind a projected placeholder, at the amount and
   * due date the admin just confirmed. A race with the monthly cron (or another
   * admin) is resolved by returning the row that WON instead of erroring out —
   * that row's amount stands, since the generator computed it from occupancy
   * rather than estimating it. `created` distinguishes the two so the caller
   * doesn't claim it wrote a row it only found.
   */
  const materialize = async (
    row: ManagementInvoiceRow,
    amountCents: number,
    dueDate: string | null
  ): Promise<{ row: ManagementInvoiceRow; created: boolean } | null> => {
    if (!row.lessor_id) {
      toast.error(
        t(
          "admin.payments.toastMissingOwner",
          "Missing owner on this invoice — cannot create it."
        )
      );
      return null;
    }
    try {
      // owner_user_id is deliberately NOT passed: on a projected row it is copied
      // from the anchor invoice, so it can be stale. The DB resolves it from
      // live data on insert (20260826190000).
      const created = await createInvoice.mutateAsync({
        lessor_id: row.lessor_id,
        amount_cents: amountCents,
        period_month: row.period_month,
        due_date: dueDate,
        // The projected row already knows which unit it forecasts; writing it
        // onto the invoice is what makes this the SAME row the monthly cron
        // would have produced, rather than a portfolio-level duplicate
        // alongside it.
        apartment_id: row.apartment_id ?? null,
        property_id: row.property_id ?? null,
        unit_title: row.__unitTitle ?? null,
        unit_name: row.__unitName ?? null,
        property_title: row.__unitPropertyTitle ?? null,
        // The forecast already knows how this unit's fee settles (it read the
        // collection mode); creating the month by hand must write the same
        // value the cron would have, or a fee that is deducted from rent
        // becomes one the owner is asked to transfer.
        settlement: row.__unitSettlement ?? row.settlement ?? "transfer",
      });
      return { row: created, created: true };
    } catch (err: any) {
      if (err?.code === "23505") {
        // The unit-level unique indexes exclude canceled rows (20260826180000),
        // so a canceled invoice no longer blocks this slot and this branch is
        // normally a race we lost. Still verify the row that won
        // is LIVE before handing it back: a 23505 can also come from another
        // constraint (e.g. the invoice_no unique key), and on a pre-migration
        // database the old index does not filter by status at all.
        // Probe the slot this insert actually targeted. An invoice is now
        // unique per (apartment, month) — or per (property, lessor, month) for
        // a unit with no apartment — so looking for a row with both ids NULL
        // would miss the apartment-level row that just won the race and report
        // a phantom "no live invoice for the month".
        let probe = supabase
          .from("management_invoices")
          .select(
            `*, lessors:lessor_id ( id, name, email, address, city_postal )`
          )
          .eq("period_month", row.period_month)
          .neq("status", "canceled");
        probe =
          row.apartment_id != null
            ? probe.eq("apartment_id", row.apartment_id)
            : row.property_id != null
              ? // The per-property key also carries settlement, and a month
                // closed early holds a remainder beside its first invoice
                // (COL-483) — either would make this probe match two rows.
                // The earliest is the month's first invoice.
                probe
                  .is("apartment_id", null)
                  .eq("property_id", row.property_id)
                  .eq("lessor_id", row.lessor_id)
                  .eq(
                    "settlement",
                    row.__unitSettlement ?? row.settlement ?? "transfer"
                  )
                  .order("created_at", { ascending: true })
                  .limit(1)
              : probe
                  .is("apartment_id", null)
                  .is("property_id", null)
                  .eq("lessor_id", row.lessor_id);
        const { data, error: recoveryError } = await probe.maybeSingle();
        // A failure here is a fresh, unrelated error, not evidence of a
        // canceled row — don't mislabel it as one.
        if (recoveryError) {
          toast.error(
            recoveryError.message ??
              t(
                "admin.payments.toastCreateFailed",
                "Could not create the invoice"
              )
          );
          return null;
        }
        if (data) {
          return { row: data as ManagementInvoiceRow, created: false };
        }
        // Nothing live found, yet the insert was still refused as a duplicate.
        // Since 20260826180000 a canceled row no longer occupies this slot, so
        // naming one as the cause would usually be wrong — the realistic causes
        // are a duplicate invoice_no, or a canceled row on a database that has
        // not taken that migration yet. Say what is actually known (refused as a
        // duplicate, no live invoice for the month) and let the admin look,
        // rather than asserting a diagnosis the constraint does not support.
        toast.error(
          t(
            "admin.payments.toastDuplicateNoLiveInvoice",
            "Could not create this invoice — the database rejected it as a duplicate, but this month has no live invoice. Check the month's invoices, including canceled ones, before retrying."
          )
        );
        return null;
      }
      toast.error(
        err?.message ??
          t("admin.payments.toastCreateFailed", "Could not create the invoice")
      );
      return null;
    }
  };

  /**
   * Open the Storno dialog, pre-filled with the issued invoice's own figures —
   * the admin is correcting them, so starting from the current values (rather
   * than blank) makes a due-date-only or small-amount correction one edit.
   */
  const openReissue = (row: ManagementInvoiceRow) => {
    setReissueAmount(((row.amount_cents || 0) / 100).toString());
    setReissueDue(row.due_date ? String(row.due_date).slice(0, 10) : "");
    setReissueReason("");
    setReissueFor(row);
  };

  const submitReissue = async () => {
    if (!reissueFor) return;
    const reason = reissueReason.trim();
    if (!reason) {
      toast.error(
        t(
          "admin.payments.toastReissueReasonRequired",
          "Give a reason — it's recorded against the cancelled invoice."
        )
      );
      return;
    }
    const parsed = parseFloat(reissueAmount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error(
        t(
          "admin.payments.toastAmountRequired",
          "Enter an amount greater than €0."
        )
      );
      return;
    }
    try {
      const replacement = await cancelAndReissue.mutateAsync({
        id: reissueFor.id,
        reason,
        amount_cents: Math.round(parsed * 100),
        due_date: reissueDue || null,
      });
      // TWO rows, not one. The cancelled invoice and its replacement are
      // different documents with different numbers, and the Activity tab is
      // per-invoice — logging only the cancellation would leave the replacement
      // with no origin story, and logging only the reissue would make the
      // original look like it silently disappeared (the invoice list hides
      // canceled rows, so the Storno has no other trace in the UI at all).
      // Each row names the other's number so the pair reads as one action.
      await logOwnerAudit({
        lessorId: reissueFor.lessor_id,
        action: "management_invoice_canceled",
        resourceType: "management_invoice",
        resourceId: String(reissueFor.id),
        oldValues: { amount_cents: reissueFor.amount_cents },
        metadata: {
          reason,
          invoice_no: reissueFor.invoice_no,
          replaced_by_invoice_no: replacement.invoice_no,
        },
      });
      await logOwnerAudit({
        lessorId: replacement.lessor_id ?? reissueFor.lessor_id,
        action: "management_invoice_reissued",
        resourceType: "management_invoice",
        resourceId: String(replacement.id),
        oldValues: { amount_cents: reissueFor.amount_cents },
        newValues: { amount_cents: replacement.amount_cents },
        metadata: {
          reason,
          invoice_no: replacement.invoice_no,
          replaces_invoice_no: reissueFor.invoice_no,
        },
      });
      invalidateOwnerAudit(reissueFor);
      // Name both numbers: the admin needs to know which invoice_no the owner
      // must now be sent, and that the old one is void rather than deleted.
      const oldNo =
        reissueFor.invoice_no ?? t("admin.payments.genericInvoice", "Invoice");
      const newNo =
        replacement.invoice_no ??
        t("admin.payments.genericNewInvoice", "a new invoice");
      toast.success(
        t(
          "admin.payments.toastReissued",
          "{old} cancelled — reissued as {new}. Send it to the owner."
        )
          .replace("{old}", oldNo)
          .replace("{new}", newNo)
      );
      setReissueFor(null);
      setReissueReason("");
    } catch (err: any) {
      // Leave the dialog open with the drafts intact — the server-side rules
      // (already canceled, paid, not yet issued) are all recoverable by the
      // admin picking a different row, and retyping the reason is a tax.
      toast.error(
        err?.message ??
          t(
            "admin.payments.toastReissueFailed",
            "Could not cancel and reissue this invoice."
          )
      );
    }
  };

  /**
   * Create an offset property's forecast month as the LIVE invoice (COL-487):
   * the generator writes it, scoped to this property and owner, so its fee
   * follows the rent collected and an early close opens a remainder. There is
   * no amount to confirm — the live-fee trigger computes it as it is inserted.
   */
  const createLiveOffsetMonth = async (row: ManagementInvoiceRow) => {
    if (!row.lessor_id || row.property_id == null) {
      toast.error(
        t(
          "admin.payments.toastMissingOwner",
          "Missing owner on this invoice — cannot create it."
        )
      );
      return;
    }
    let result: Awaited<ReturnType<typeof generatePropertyMonth.mutateAsync>>;
    try {
      result = await generatePropertyMonth.mutateAsync({
        lessor_id: row.lessor_id,
        property_id: Number(row.property_id),
        period_month: String(row.period_month).slice(0, 10),
        settlement: "offset",
      });
    } catch (err: any) {
      toast.error(
        err?.message ??
          t("admin.payments.toastCreateFailed", "Could not create the invoice")
      );
      return;
    }
    if (!result.row) {
      toast.error(
        t(
          "admin.payments.toastLiveOffsetNotGenerated",
          "No invoice was created: the generator has nothing to bill against collected rent for this property this month. Check its occupied rooms, fee and collection settings, and whether the month is already billed another way."
        )
      );
      return;
    }
    toast[result.created ? "success" : "info"](
      result.created
        ? t("admin.payments.toastInvoiceCreated", "Invoice created.")
        : t(
            "admin.payments.toastInvoicedMeantime",
            "This month was invoiced in the meantime — keeping the generated amount."
          )
    );
    setEditFor(null);
    setAfterCreate(editThen ? { kind: editThen, row: result.row } : null);
    setEditThen(null);
  };

  const submitEdit = async () => {
    if (!editFor) return;
    if (isOffsetPropertyForecast(editFor)) {
      await createLiveOffsetMonth(editFor);
      return;
    }
    const parsed = parseFloat(editAmount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error(
        t(
          "admin.payments.toastAmountRequired",
          "Enter an amount greater than €0."
        )
      );
      return;
    }
    const cents = Math.round(parsed * 100);
    const due = editDue || null;
    if (editFor.__projected) {
      const result = await materialize(editFor, cents, due);
      // materialize() has already reported why; leave the dialog open so the
      // admin can retry or cancel rather than losing what they typed.
      if (!result) return;
      toast[result.created ? "success" : "info"](
        result.created
          ? t("admin.payments.toastInvoiceCreated", "Invoice created.")
          : t(
              "admin.payments.toastInvoicedMeantime",
              "This month was invoiced in the meantime — keeping the generated amount."
            )
      );
      setEditFor(null);
      // Hand the resulting row to the action the admin actually came for, once
      // this dialog is gone (see afterCreate).
      setAfterCreate(editThen ? { kind: editThen, row: result.row } : null);
      setEditThen(null);
      return;
    }
    try {
      await editInvoice.mutateAsync({
        id: editFor.id,
        amount_cents: cents,
        due_date: due,
      });
      toast.success(
        t("admin.payments.toastInvoiceUpdated", "Invoice updated.")
      );
      setEditFor(null);
    } catch (err: any) {
      // COL-474: a live offset invoice's fee follows collected rent and
      // cannot be overridden by hand — say so instead of the raw DB error.
      if (isOffsetLiveFeeEditError(err)) {
        toast.error(
          t(
            "admin.payments.offsetLiveFeeEditRefused",
            "This offset invoice's fee follows the rent collected and can't be overridden or saved by hand. It becomes final when the payout is confirmed or the invoice is sent."
          )
        );
        return;
      }
      toast.error(
        err?.message ??
          t("admin.payments.toastSaveChangesFailed", "Could not save changes.")
      );
    }
  };

  const openDelete = (row: ManagementInvoiceRow) => {
    setReasonDraft("");
    setDeleteFor(row);
  };
  const submitDelete = async () => {
    if (!deleteFor || !reasonDraft.trim()) return;
    try {
      const { deletedAt } = await deleteProof.mutateAsync({
        id: deleteFor.id,
        proof_url: deleteFor.proof_url,
        reason: reasonDraft.trim(),
        metadata: deleteFor.metadata ?? {},
      });
      // `deletion_at` ties this row to the entry deleteProof appended to
      // metadata.proof_deletions, so the feed renders the deletion once — with
      // the admin's name attached — instead of twice.
      await logOwnerAudit({
        lessorId: deleteFor.lessor_id,
        action: "management_invoice_proof_deleted",
        resourceType: "management_invoice",
        resourceId: String(deleteFor.id),
        metadata: { reason: reasonDraft.trim(), deletion_at: deletedAt },
      });
      invalidateOwnerAudit(deleteFor);
      toast.success(t("admin.payments.toastProofDeleted", "Proof deleted."));
      setDeleteFor(null);
      setReasonDraft("");
    } catch (err: any) {
      toast.error(
        err?.message ?? t("admin.payments.toastDeleteFailed", "Delete failed")
      );
    }
  };

  const openInvoice = async (row: ManagementInvoiceRow) => {
    const ctx = {
      ownerName: row.lessors?.name,
      ownerEmail: row.lessors?.email,
      ownerLines: [row.lessors?.address, row.lessors?.city_postal].filter(
        Boolean
      ) as string[],
      // Names the APARTMENT this invoice bills; falls back to the property, or
      // (legacy consolidated) to the portfolio — see invoicePropertyLabel.
      propertyTitle: invoicePropertyLabel(row, t).title,
      propertyAddress:
        [row.properties?.title, row.properties?.city]
          .filter(Boolean)
          .join(", ") ||
        invoicePropertyLabel(row, t).full ||
        "",
      bank: platformBankFor(row),
    };

    // Itemized (2+ property) invoices can't be represented by the fixed-layout
    // stamped template (one amount field) — they'd show a single lump sum that
    // contradicts the per-property line items in the emailed invoice. For those
    // we skip the template and use the code-generated itemized document, so the
    // preview matches exactly what the owner receives.
    const props = Array.isArray(
      (row.fee_basis as { properties?: unknown } | null)?.properties
    )
      ? (row.fee_basis as { properties: unknown[] }).properties
      : [];
    const itemized = props.length > 1;

    // An offset invoice can't be represented by the stamped template either:
    // "Seller/bank details are pre-printed on the template itself" (see
    // AdminInvoiceTemplates.tsx) — the overlay mechanism only replaces the
    // named variable fields, so it has no way to blank out a template's own
    // pre-printed Bankverbindung/transfer instructions. Only the
    // code-generated document (managementInvoiceHtml / InvoiceDocument) is
    // actually settlement-aware, matching the same skip send-invoice's
    // buildTemplateAttachment applies for the emailed copy.
    const offset = isOffsetInvoice(row);

    // A sent-or-paid invoice is a settled legal document — re-stamping it
    // against whatever template happens to be the default *today* would
    // silently change what "View" shows for an invoice that was already
    // issued (the template can be swapped anytime after the fact). Viewing
    // it is therefore just the deterministic, row-only render — the live
    // template lookup/stamp only applies to a still-editable draft, where
    // reflecting template corrections before send is the point. Reuses
    // canEditManagementInvoice so this stays in lockstep with the same
    // not-paid-and-not-sent gate the Edit action already enforces — a
    // pending-but-already-sent invoice (still shown in Invoice schedule) must
    // not re-stamp either, only a true unsent draft may.
    const frozen = !canEditManagementInvoice(row);

    // Use the DEFAULT invoice template (stamp the row's values onto it). Fall
    // back to the code-generated invoice when no default template is configured,
    // so uploading a non-default (e.g. test) template never changes what owners
    // see or receive.
    if (!itemized && !frozen && !offset) {
      setStamping(true);
      try {
        const { data: tpl } = await supabase
          .from("invoice_templates")
          .select("template_pdf_url, vector_overlay_config")
          .eq("is_default", true)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        const fields = (
          tpl?.vector_overlay_config as { fields?: PlacedField[] } | null
        )?.fields;
        if (tpl?.template_pdf_url && fields?.length) {
          // Through the storage API, not the stored public URL: the
          // contracts bucket is going private (COL-436).
          const blob = await downloadStoredFile(
            tpl.template_pdf_url,
            "contracts"
          ).catch(() => {
            throw new Error(
              t(
                "admin.payments.errLoadTemplatePdf",
                "Could not load the invoice template PDF"
              )
            );
          });
          const bytes = new Uint8Array(await blob.arrayBuffer());
          const values = managementInvoiceFieldValues(row, ctx);
          const out = await stampInvoicePdf(bytes, fields, values, {
            whiteout: true,
          });
          const url = URL.createObjectURL(
            new Blob([out], { type: "application/pdf" })
          );
          closePdfPreview();
          pdfUrlRef.current = url;
          setPdfPreview({ url, row });
          return;
        }
      } catch (err: any) {
        toast.error(
          err?.message ??
            t(
              "admin.payments.toastTemplateBuildFailed",
              "Could not build invoice from template"
            )
        );
      } finally {
        setStamping(false);
      }
    }

    // Itemized invoice, a sent/paid one, or no template (or it failed) →
    // code-generated preview from the row's own frozen data.
    setPreview({ data: buildManagementInvoiceData(row, ctx), row });
  };

  /**
   * Settle an invoice, with the owner-audit row the feed reads.
   *
   * Hoisted out of the button that used to own it: the same action is now
   * offered from the hub row's primary button AND from its overflow menu
   * (whichever is not showing the other), and an audit payload copied into two
   * call sites is one edit away from disagreeing with itself.
   */
  const markPaidWithAudit = (r: ManagementInvoiceRow) =>
    markPaid.mutate(r, {
      // COL-483: a canceled invoice (such as a remainder absorbed when its
      // payout was undone) is never marked paid — say so.
      onError: (err) => {
        toast.error(
          (err as { code?: unknown } | null)?.code === MARK_PAID_CANCELED
            ? t(
                "admin.payments.markPaidCanceledRefused",
                "This invoice has been canceled, so it can't be marked paid."
              )
            : ((err as { message?: string } | null)?.message ??
                t(
                  "admin.payments.toastMarkPaidFailed",
                  "Could not mark this invoice paid."
                ))
        );
      },
      onSuccess: () => {
        if (isLiveOffsetInvoice(r)) {
          void fetchOpenRemainderNo(r.id).then((no) => {
            if (no) notifyRemainderOpened(no, t);
          });
        }
        void logOwnerAudit({
          lessorId: r.lessor_id,
          action: "management_invoice_marked_paid",
          resourceType: "management_invoice",
          resourceId: String(r.id),
          newValues: {
            invoice_no: r.invoice_no,
            amount_cents: r.amount_cents,
          },
        }).then(() => invalidateOwnerAudit(r));
      },
    });

  const unmarkPaidWithAudit = (r: ManagementInvoiceRow) =>
    unmarkPaid.mutate(r.id, {
      // COL-483: undoing a payout that opened a remainder absorbs it, unless
      // a later invoice of the month was paid out, sent or reissued — the DB
      // then refuses.
      onError: (err) => {
        toast.error(
          (err as { code?: unknown } | null)?.code === UNMARK_PAID_NOT_PAID
            ? t(
                "admin.payments.unmarkNotPaidRefused",
                "This invoice is no longer marked paid — it may have been canceled or unmarked elsewhere. Reload to see where it stands."
              )
            : isRemainderReopenRefusal(err)
              ? t(
                  "admin.payments.toastUnmarkRefusedRemainder",
                  "This payout can't be undone: a later invoice for this month has already been paid out, sent or reissued."
                )
              : ((err as { message?: string } | null)?.message ??
                t(
                  "admin.payments.toastUnmarkFailed",
                  "Could not undo this payment."
                ))
        );
      },
      onSuccess: () => {
        void logOwnerAudit({
          lessorId: r.lessor_id,
          action: "management_invoice_marked_unpaid",
          resourceType: "management_invoice",
          resourceId: String(r.id),
          oldValues: { status: "paid", paid_at: r.paid_at },
          newValues: { status: "pending", paid_at: null },
        }).then(() => invalidateOwnerAudit(r));
      },
    });

  /**
   * The ONE action a payments-hub row is waiting for, rendered as its primary
   * button; everything else lives in the row's overflow menu.
   *
   * Null on a settled invoice — nothing is owed, so nothing is offered, and
   * Unmark moves into the menu where an undo belongs. That is what lets a
   * table of mostly-paid rows read as calm: a button appears only where
   * somebody still has to do something. Null on a FORECAST row too: it has no
   * database row to act on, and every one of its actions has to route through
   * the create dialog first (see the menu's `projected` branch).
   *
   * The order matters. `upload-proof` sits last as the fallback because Mark
   * paid is proof-gated: offering it on a row with no proof is a button that
   * cannot be pressed, which is exactly what this replaced.
   */
  const hubPrimaryFor = (
    r: ManagementInvoiceRow,
    hasProof: boolean,
    hasNote: boolean
  ): "confirm-offset" | "mark-paid" | "add-note" | "upload-proof" | null => {
    if (r.__projected || r.__unitBillable === false) return null;
    if (r.status === "paid") return null;
    if (r.status === "canceled") return null;
    // An offset invoice has no transfer to prove, so the proof → note → Mark
    // paid chain can never complete for it; "Record payout" settles it
    // in one write (see confirmOffset). Both are super-admin-only (COL-461) — a
    // plain admin sees no primary button once the row is otherwise ready,
    // rather than one that would only fail server-side.
    if (isOffsetInvoice(r)) return isSuperAdmin ? "confirm-offset" : null;
    if (hasProof && hasNote) return isSuperAdmin ? "mark-paid" : null;
    if (hasProof) return "add-note";
    return "upload-proof";
  };

  /**
   * The row's overflow menu — ONE kebab holding everything the row can do
   * except the single action its primary button already offers.
   *
   * It used to be a row of labeled pills (View · Edit · Upload · Notes, plus
   * Replace/Proof once a proof existed), which was three to five controls wide
   * and wrapped into a second line inside the modal — the widest thing in the
   * table, for the least-used column. Everything the pills offered is here,
   * one tap further in, and the table gets its width back for the figures.
   *
   * Shared by both surfaces since the payments hub adopted it too. `hub` adds
   * the items the owner drawer withholds on purpose (Delete proof, Mark paid
   * and Unmark, all of which either destroy something or undo it), and
   * `primary` drops whichever item the hub row is already showing as a button,
   * so no action appears twice in one row.
   *
   * A forecast row for a unit the generator will NOT bill (`__unitBillable ===
   * false` — no occupied rooms, master rent unset, …) gets
   * the same kebab so the Action column reads as one control down the whole
   * table, but its menu holds only Payment configuration: there is no invoice
   * to view or edit, and offering "Create invoice" would contradict the very
   * setting the status pill names. Its row renders no kebab at all when the
   * row names no property to configure.
   */
  const renderInvoiceMenu = (
    r: ManagementInvoiceRow,
    opts: {
      hub?: boolean;
      primary?: ReturnType<typeof hubPrimaryFor>;
      /** What "Record payout" will record — see renderActions. */
      netPayoutCents?: number | null;
    } = {}
  ): ReactNode => {
    const hub = !!opts.hub;
    const primary = opts.primary ?? null;
    const isPaid = r.status === "paid";
    if (r.__unitBillable === false) {
      if (!paymentConfigurationPath(r)) return null;
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 shrink-0 p-0 text-muted-foreground data-[state=open]:bg-muted"
              aria-label={t("admin.payments.billingOptions", "Billing options")}
              title={t("admin.payments.billingOptions", "Billing options")}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              {t("admin.payments.notBilledThisMonth", "Not billed this month")}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <PaymentConfigurationAnchor row={r} className="cursor-pointer">
                <Settings2 className="mr-2 h-4 w-4" />
                {t(
                  "admin.payments.paymentConfiguration",
                  "Payment configuration"
                )}
              </PaymentConfigurationAnchor>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    const busy = uploadProof.isPending && target?.id === r.id;
    const hasProof = !!r.proof_url;
    const proofNote = (r.metadata as Record<string, unknown>)?.proof_note;
    const hasNote = typeof proofNote === "string" && proofNote.trim() !== "";
    // A projected placeholder has no database row yet, so every item routes
    // through the create-then-act flow (openEdit) rather than acting on a row
    // that does not exist.
    const projected = !!r.__projected;
    // Settled by deducting the fee from collected rent — offered until the
    // invoice is actually settled, and never on a forecast row.
    //
    // `canceled` is excluded as well as `paid`: confirmOffset refuses a voided
    // row server-side, so offering the item on one puts an action in the menu
    // that can only ever fail. (The list query filters canceled rows out, so
    // this is belt-and-braces — but the gate should be right on its own.)
    const canConfirmOffset =
      !projected &&
      isOffsetInvoice(r) &&
      r.status !== "paid" &&
      r.status !== "canceled" &&
      isSuperAdmin;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 shrink-0 p-0 text-muted-foreground data-[state=open]:bg-muted"
            aria-label={
              projected
                ? t(
                    "admin.payments.tooltipCreateInvoice",
                    "Create this invoice"
                  )
                : t("admin.payments.invoiceActions", "Invoice actions")
            }
            title={
              projected
                ? t(
                    "admin.payments.tooltipCreateInvoice",
                    "Create this invoice"
                  )
                : t("admin.payments.invoiceActions", "Invoice actions")
            }
          >
            {busy || (stamping && !projected) ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MoreHorizontal className="h-4 w-4" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            {projected
              ? t("admin.payments.notYetIssued", "Not yet issued")
              : (r.invoice_no ??
                t("admin.payments.thisInvoice", "This invoice"))}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {projected ? (
            <>
              <DropdownMenuItem onSelect={() => openEdit(r)}>
                <Pencil className="mr-2 h-4 w-4" />
                {t("admin.payments.menuCreateInvoice", "Create invoice")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => openEdit(r, "proof")}>
                <Upload className="mr-2 h-4 w-4" />
                {t(
                  "admin.payments.menuCreateThenUploadProof",
                  "Create, then upload proof"
                )}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => openEdit(r, "note")}>
                <StickyNote className="mr-2 h-4 w-4" />
                {t(
                  "admin.payments.menuCreateThenAddNote",
                  "Create, then add a note"
                )}
              </DropdownMenuItem>
            </>
          ) : (
            <>
              <DropdownMenuItem
                onSelect={() => openInvoice(r)}
                disabled={stamping}
              >
                <Eye className="mr-2 h-4 w-4" />
                {t("admin.payments.menuViewInvoice", "View invoice")}
              </DropdownMenuItem>
              {/* A live offset draft's fee follows collected rent and the DB
                  refuses a hand edit of it, so there is nothing to edit. */}
              {canEditManagementInvoice(r) && !isLiveOffsetInvoice(r) && (
                <DropdownMenuItem onSelect={() => openEdit(r)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("admin.payments.menuEditAmount", "Edit amount")}
                </DropdownMenuItem>
              )}
              {primary !== "upload-proof" && (
                <DropdownMenuItem onSelect={() => pickProof(r)} disabled={busy}>
                  <Upload className="mr-2 h-4 w-4" />
                  {hasProof
                    ? t("admin.payments.menuReplaceProof", "Replace proof")
                    : t("admin.payments.menuUploadProof", "Upload proof")}
                </DropdownMenuItem>
              )}
              {hasProof && (
                <DropdownMenuItem
                  // proof_url is a path in the contracts bucket, which is going
                  // private: signed on click, never a public link (COL-436).
                  onSelect={() =>
                    void openStoredFile(
                      r.proof_url as string,
                      "contracts"
                    ).then((ok) => {
                      if (!ok)
                        toast.error(
                          t(
                            "admin.docs.openFailed",
                            "Could not open the document."
                          )
                        );
                    })
                  }
                  className="text-teal-600 focus:text-teal-700"
                >
                  <Paperclip className="mr-2 h-4 w-4" />
                  {t("admin.payments.menuOpenProof", "Open proof")}
                </DropdownMenuItem>
              )}
              {primary !== "add-note" && (
                <DropdownMenuItem
                  onSelect={() => openNote(r)}
                  className={cn(hasNote && "text-emerald-600")}
                >
                  <StickyNote className="mr-2 h-4 w-4" />
                  {hasNote
                    ? t("admin.payments.menuEditNote", "Edit note")
                    : t("admin.payments.menuAddNote", "Add note")}
                </DropdownMenuItem>
              )}
              {canConfirmOffset && primary !== "confirm-offset" && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() =>
                      openOffsetConfirm(r, opts.netPayoutCents ?? null)
                    }
                  >
                    <ArrowLeftRight className="mr-2 h-4 w-4" />
                    {t("admin.payments.menuConfirmOffset", "Record payout")}
                  </DropdownMenuItem>
                </>
              )}
              {/* Hub-only, and the counterpart of Edit amount above: exactly
                  one of the two is ever offered, because a draft is corrected
                  in place while an issued invoice must be voided and reissued
                  rather than restated behind an invoice_no the owner already
                  holds. The owner drawer withholds it for the same reason it
                  withholds Unmark — it voids a document. */}
              {hub && canCancelAndReissueManagementInvoice(r) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => openReissue(r)}
                    className="text-amber-600 focus:text-amber-700"
                  >
                    <FileX className="mr-2 h-4 w-4" />
                    {t("admin.payments.cancelReissue", "Cancel & reissue")}
                  </DropdownMenuItem>
                </>
              )}
            </>
          )}
          {/* Hub-only: destructive and undo actions the owner drawer withholds
              on purpose. They belong behind the kebab rather than in the row,
              where an accidental click costs a settled invoice its proof or
              its paid status. */}
          {hub && hasProof && (
            <DropdownMenuItem
              onSelect={() => openDelete(r)}
              className="text-rose-600 focus:text-rose-700"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t(
                "admin.paymentsInvoiceActions.deleteProofTitle",
                "Delete proof"
              )}
            </DropdownMenuItem>
          )}
          {hub &&
            isSuperAdmin &&
            !isPaid &&
            hasProof &&
            hasNote &&
            primary !== "mark-paid" && (
              <DropdownMenuItem onSelect={() => markPaidWithAudit(r)}>
                <Check className="mr-2 h-4 w-4" />
                {t("admin.paymentsInvoiceActions.markPaidButton", "Mark paid")}
              </DropdownMenuItem>
            )}
          {hub && isPaid && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => unmarkPaidWithAudit(r)}>
                <Undo2 className="mr-2 h-4 w-4" />
                {t("admin.paymentsInvoiceActions.unmarkButton", "Unmark")}
              </DropdownMenuItem>
            </>
          )}
          {/* The settings that decided whether and how this unit is billed —
              collection mode, revenue model, master rent, the offset-invoicing
              switch (COL-287) — one step away from the row they produced.
              Offered on forecast rows too: a 0,00 € row is usually there
              BECAUSE of one of those settings. */}
          {paymentConfigurationPath(r) && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <PaymentConfigurationAnchor row={r} className="cursor-pointer">
                  <Settings2 className="mr-2 h-4 w-4" />
                  {t(
                    "admin.payments.paymentConfiguration",
                    "Payment configuration"
                  )}
                </PaymentConfigurationAnchor>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  /**
   * The payments-hub row's action set: ONE primary button and the kebab.
   *
   * It used to be five to seven labelled pills per row, which pushed the Action
   * column off the right edge and made the table scroll sideways to reach the
   * button an admin came for. Everything the pills offered is still here — see
   * renderInvoiceMenu — with the single step the row is actually waiting on
   * lifted out in front of it.
   *
   * `compact` no longer changes what is offered, only how wide it is: there is
   * one button either way now, so the flag survives for its callers rather than
   * for the layout it used to save.
   */
  const renderActions = (
    r: ManagementInvoiceRow,
    opts?: {
      compact?: boolean;
      ownerStyle?: boolean;
      netPayoutCents?: number | null;
    }
  ): ReactNode => {
    const netPayoutCents = opts?.netPayoutCents ?? null;
    if (opts?.ownerStyle) return renderInvoiceMenu(r, { netPayoutCents });
    const busy = uploadProof.isPending && target?.id === r.id;
    const hasProof = !!r.proof_url;
    const proofNote = (r.metadata as Record<string, unknown>)?.proof_note;
    const hasNote = typeof proofNote === "string" && proofNote.trim() !== "";
    const primary = hubPrimaryFor(r, hasProof, hasNote);

    /**
     * The row's single visible action. Everything else — view, edit, proof,
     * notes, cancel & reissue, payment configuration, and the undo actions —
     * is one tap further in, behind the kebab beside it.
     *
     * A settled row, and a forecast row that has no invoice yet, render
     * nothing here at all: the buttons left on screen are exactly the rows
     * still waiting on someone.
     */
    const primaryButton = (() => {
      switch (primary) {
        case "confirm-offset":
          return (
            <Button
              size="sm"
              onClick={() => openOffsetConfirm(r, netPayoutCents)}
              title={t(
                "admin.payments.tooltipConfirmOffset",
                "Record that the net rent went out, and settle this fee against the rent collected for this period"
              )}
            >
              <ArrowLeftRight className="mr-1 h-4 w-4" />
              {t("admin.payments.menuConfirmOffset", "Record payout")}
            </Button>
          );
        case "mark-paid":
          return (
            <Button
              size="sm"
              onClick={() => markPaidWithAudit(r)}
              title={t("admin.payments.tooltipMarkPaid", "Mark paid")}
            >
              <Check className="mr-1 h-4 w-4" />
              {t("admin.payments.actionMarkPaid", "Mark paid")}
            </Button>
          );
        case "add-note":
          return (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => openNote(r)}
              title={t(
                "admin.payments.tooltipAddRequiredNote",
                "Add the required note to continue"
              )}
            >
              <StickyNote className="mr-1 h-4 w-4" />
              {t("admin.payments.menuAddNote", "Add note")}
            </Button>
          );
        case "upload-proof":
          // Mark paid is proof-gated, so a row with no proof used to show it
          // permanently disabled with the real instruction hidden in a
          // tooltip. Offer the step that IS available instead.
          return (
            <Button
              size="sm"
              onClick={() => pickProof(r)}
              disabled={busy}
              title={t(
                "admin.payments.tooltipUploadProof",
                "Upload payment proof"
              )}
            >
              {busy ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-1 h-4 w-4" />
              )}
              {t("admin.payments.menuUploadProof", "Upload proof")}
            </Button>
          );
        default:
          return null;
      }
    })();

    return (
      <div className="flex items-center justify-end gap-1.5">
        {primaryButton}
        {renderInvoiceMenu(r, { hub: true, primary, netPayoutCents })}
      </div>
    );
  };

  const overlays = (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={onFile}
      />

      <InvoicePreviewDialog
        open={!!preview}
        onOpenChange={(v) => !v && setPreview(null)}
        data={preview?.data ?? null}
        sentAt={preview?.row.sent_at ?? null}
        sending={sendInvoice.isPending}
        onSend={() => preview && sendInvoice.mutate(preview.row)}
      />

      {/* Stamped-template invoice (from the uploaded invoice template) */}
      <Dialog open={!!pdfPreview} onOpenChange={(v) => !v && closePdfPreview()}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {t("admin.payments.invoiceDialogTitle", "Invoice {no}").replace(
                "{no}",
                pdfPreview?.row.invoice_no ?? ""
              )}
            </DialogTitle>
          </DialogHeader>
          {pdfPreview && (
            <iframe
              src={pdfPreview.url}
              title={t("admin.payments.invoice", "Invoice")}
              className="h-[72vh] w-full rounded-md border"
            />
          )}
          <DialogFooter className="flex-row items-center justify-end gap-2 sm:gap-2">
            {pdfPreview?.row.sent_at ? (
              <span className="mr-auto text-xs text-muted-foreground">
                {t("admin.payments.sentOn", "Sent {date}").replace(
                  "{date}",
                  new Date(pdfPreview.row.sent_at).toLocaleString(
                    language === "de" ? "de-DE" : "en-GB"
                  )
                )}
              </span>
            ) : null}
            <a
              href={pdfPreview?.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-accent"
            >
              {t("admin.payments.openInNewTab", "Open in new tab")}
            </a>
            <a
              href={pdfPreview?.url}
              download={`${pdfPreview?.row.invoice_no ?? "invoice"}.pdf`}
              className="inline-flex items-center justify-center rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-accent"
            >
              {t("admin.payments.downloadPdf", "Download PDF")}
            </a>
            <Button
              onClick={() => pdfPreview && sendInvoice.mutate(pdfPreview.row)}
              disabled={
                sendInvoice.isPending ||
                !pdfPreview ||
                !canSendInvoice(pdfPreview.row)
              }
              title={
                pdfPreview && canSendInvoice(pdfPreview.row)
                  ? undefined
                  : t(
                      "admin.payments.noBankAccountWarning",
                      "No coliviq bank account resolves for this property — set a platform bank account (or the property's) before sending"
                    )
              }
            >
              {sendInvoice.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {t("admin.payments.sendInvoice", "Send invoice")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mandatory note after a proof is uploaded (or to view/edit it later) */}
      <Dialog
        open={!!noteFor}
        onOpenChange={(v) => {
          if (!v) {
            setNoteFor(null);
            setNoteDraft("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                <StickyNote className="h-5 w-5" />
              </span>
              {t("admin.payments.paymentNoteTitle", "Payment note")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "admin.payments.paymentNoteDescPrefix",
                "Record a note about the payment proof for"
              )}{" "}
              <span className="font-medium text-foreground">
                {noteFor?.invoice_no}
              </span>
              .{" "}
              {t(
                "admin.payments.paymentNoteDescSuffix",
                "A note is required before the invoice can be marked paid."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="proof-note">
              {t("admin.payments.note", "Note")}{" "}
              <span className="text-rose-500">*</span>
            </Label>
            <Textarea
              id="proof-note"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              rows={4}
              autoFocus
              placeholder={t(
                "admin.payments.notePlaceholder",
                "e.g. Bank transfer received on 12 Jul 2026, ref MF-2026-08-001…"
              )}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setNoteFor(null);
                setNoteDraft("");
              }}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              onClick={submitNote}
              disabled={!noteDraft.trim() || addProofNote.isPending}
            >
              {addProofNote.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              )}
              {t("admin.payments.saveNote", "Save note")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settle an OFFSET invoice against collected rent. One dialog, one
          write: the note IS the evidence here, because no bank transfer exists
          to upload — see the paid gate in 20260907110000 §3. */}
      <Dialog
        open={!!offsetFor}
        onOpenChange={(v) => {
          if (!v) {
            setOffsetFor(null);
            setOffsetNote("");
            setOffsetNetPayout(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-100 text-sky-600">
                <ArrowLeftRight className="h-5 w-5" />
              </span>
              {t("admin.payments.menuConfirmOffset", "Record payout")}
            </DialogTitle>
            <DialogDescription>
              <span className="font-medium text-foreground">
                {offsetFor?.invoice_no}
              </span>{" "}
              {t(
                "admin.payments.offsetConfirmDescription",
                "is settled by deducting the fee from the rent coliviq collected for this period — the owner transfers nothing, and is paid the net. Confirm once that net rent has gone out: it records the note below and marks the invoice paid."
              )}
            </DialogDescription>
          </DialogHeader>
          {offsetNetPayout !== null && (
            <div className="rounded-md border bg-muted/40 px-3 py-2">
              <div className="flex items-baseline justify-between gap-3 text-sm font-semibold">
                <span>
                  {t("admin.payments.offsetConfirmNetPayout", "Net payout")}
                </span>
                <span className="tabular-nums">
                  {formatCents(offsetNetPayout, offsetFor?.currency)}
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {t(
                  "admin.payments.offsetConfirmNetPayoutHint",
                  "This amount is saved as what was paid out — check it matches the transfer you made."
                )}
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="offset-note">
              {t("admin.payments.note", "Note")}{" "}
              <span className="text-rose-500">*</span>
            </Label>
            <Textarea
              id="offset-note"
              value={offsetNote}
              onChange={(e) => setOffsetNote(e.target.value)}
              rows={4}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              {t(
                "admin.payments.offsetNoteHint",
                "This note is the only record of the deduction — say which rent it came out of."
              )}
            </p>
            {offsetSiblings.length > 0 && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {t(
                  "admin.payments.offsetConfirmSiblingUnpaid",
                  "{invoices} for the same month is also waiting for its payout. If this transfer covers it too, confirm it as well."
                ).replace(
                  "{invoices}",
                  offsetSiblings.map((o) => o.invoice_no ?? "—").join(", ")
                )}
              </p>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setOffsetFor(null);
                setOffsetNote("");
                setOffsetNetPayout(null);
              }}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              onClick={submitOffsetConfirm}
              disabled={!offsetNote.trim() || confirmOffset.isPending}
            >
              {confirmOffset.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              )}
              {t("admin.payments.offsetConfirmSubmit", "Confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel + reissue (Storno) — the only route by which an issued
          invoice's figures change. The old number stays on file, voided. */}
      <Dialog
        open={!!reissueFor}
        onOpenChange={(v) => {
          if (!v) {
            setReissueFor(null);
            setReissueReason("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                <FileX className="h-5 w-5" />
              </span>
              {t(
                "admin.payments.cancelReissueTitle",
                "Cancel & reissue invoice"
              )}
            </DialogTitle>
            <DialogDescription>
              <span className="font-medium text-foreground">
                {reissueFor?.invoice_no}
              </span>{" "}
              {t(
                "admin.payments.cancelReissueDesc",
                "has already been sent to the owner, so its figures can't be changed. It will be cancelled and kept on file, and a replacement issued with a new invoice number — which you'll need to send."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="reissue-reason">
                {t(
                  "admin.payments.reasonForCancelling",
                  "Reason for cancelling"
                )}{" "}
                <span className="text-rose-500">*</span>
              </Label>
              <Textarea
                id="reissue-reason"
                value={reissueReason}
                onChange={(e) => setReissueReason(e.target.value)}
                rows={3}
                autoFocus
                placeholder={t(
                  "admin.payments.reissueReasonPlaceholder",
                  "e.g. Fee calculated on the wrong occupancy for August…"
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reissue-amount">
                {t("admin.payments.correctedAmount", "Corrected amount (€)")}
              </Label>
              <Input
                id="reissue-amount"
                type="number"
                min="0"
                step="0.01"
                value={reissueAmount}
                onChange={(e) => setReissueAmount(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reissue-due">
                {t("admin.payments.dueDate", "Due date")}
              </Label>
              <Input
                id="reissue-due"
                type="date"
                value={reissueDue}
                onChange={(e) => setReissueDue(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setReissueFor(null);
                setReissueReason("");
              }}
            >
              {t("admin.payments.keepInvoice", "Keep invoice")}
            </Button>
            <Button
              onClick={submitReissue}
              disabled={!reissueReason.trim() || cancelAndReissue.isPending}
            >
              {cancelAndReissue.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              )}
              {t("admin.payments.cancelReissue", "Cancel & reissue")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mandatory reason when deleting a proof (kept as an audit trail) */}
      <Dialog
        open={!!deleteFor}
        onOpenChange={(v) => {
          if (!v) {
            setDeleteFor(null);
            setReasonDraft("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                <AlertTriangle className="h-5 w-5" />
              </span>
              {t("admin.payments.deleteProofTitle", "Delete payment proof")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "admin.payments.deleteProofDescPrefix",
                "This removes the uploaded proof for"
              )}{" "}
              <span className="font-medium text-foreground">
                {deleteFor?.invoice_no}
              </span>{" "}
              {t(
                "admin.payments.deleteProofDescSuffix",
                "and its note. Give a reason — it's recorded for audit."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="delete-reason">
              {t("admin.payments.reasonForDeletion", "Reason for deletion")}{" "}
              <span className="text-rose-500">*</span>
            </Label>
            <Textarea
              id="delete-reason"
              value={reasonDraft}
              onChange={(e) => setReasonDraft(e.target.value)}
              rows={4}
              autoFocus
              placeholder={t(
                "admin.payments.deleteReasonPlaceholder",
                "e.g. Wrong file uploaded / illegible / belongs to another invoice…"
              )}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteFor(null);
                setReasonDraft("");
              }}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={submitDelete}
              disabled={!reasonDraft.trim() || deleteProof.isPending}
            >
              {deleteProof.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              )}
              {t("admin.payments.deleteProof", "Delete proof")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Amount/due-date edit — for a real invoice this updates it in place;
          for a projected placeholder it creates the real invoice. */}
      <Dialog
        open={!!editFor}
        onOpenChange={(v) => {
          if (!v) {
            setEditFor(null);
            setEditThen(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Pencil className="h-5 w-5" />
              </span>
              {editFor?.__projected
                ? t("admin.payments.createInvoiceTitle", "Create invoice")
                : t("admin.payments.editInvoiceTitle", "Edit invoice")}
            </DialogTitle>
            <DialogDescription>
              {editFor && isOffsetPropertyForecast(editFor)
                ? t(
                    "admin.payments.notInvoicedYetLiveOffset",
                    "This month hasn't been invoiced yet. Creating it writes the live invoice: its fee follows the rent collected and keeps updating until the payout is confirmed or the invoice is sent."
                  )
                : editFor?.__projected
                  ? // The amount shown is carried from the owner's last invoice, so
                    // say so — it is an estimate until this month's occupancy is
                    // actually billed, and confirming it here is what makes it real.
                    `${
                      editThen === "proof"
                        ? t(
                            "admin.payments.notInvoicedYetProof",
                            "This month hasn't been invoiced yet. Confirm the amount to create it, then pick the payment proof."
                          )
                        : editThen === "note"
                          ? t(
                              "admin.payments.notInvoicedYetNote",
                              "This month hasn't been invoiced yet. Confirm the amount to create it, then add your note."
                            )
                          : t(
                              "admin.payments.notInvoicedYetGeneric",
                              "This month hasn't been invoiced yet — confirm the amount and due date to create it now."
                            )
                    } ${t(
                      "admin.payments.amountCarriedHint",
                      "The amount is carried from the last invoice; adjust it if this month differs."
                    )}`
                  : t(
                      "admin.payments.updateAmountDueFor",
                      "Update the amount or due date for {invoice}."
                    ).replace(
                      "{invoice}",
                      editFor?.invoice_no ??
                        t("admin.payments.genericInvoice", "Invoice")
                    )}
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const consolidatedProps = Array.isArray(
              (editFor?.fee_basis as { properties?: unknown[] } | null)
                ?.properties
            )
              ? (editFor!.fee_basis as { properties: unknown[] }).properties
              : [];
            const isConsolidated = consolidatedProps.length > 1;
            // No total to change on a live offset month (COL-487).
            if (
              !isConsolidated ||
              (editFor && isOffsetPropertyForecast(editFor))
            )
              return null;
            return (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {t(
                  "admin.payments.multiPropertyWarning",
                  "This invoice covers multiple properties. Changing the total here won't update the per-property breakdown on the itemized document — the Net/VAT preview below is therefore not shown, since it would also disagree with that document."
                )}
              </p>
            );
          })()}
          <div className="space-y-3">
            {/* A live offset month has no amount to type: the generator
                writes it and the fee follows the rent collected (COL-487). */}
            {!(editFor && isOffsetPropertyForecast(editFor)) && (
              <div className="space-y-1.5">
                <Label htmlFor="invoice-edit-amount">
                  {t(
                    "admin.payments.grossAmountLabel",
                    "Gross amount (€, incl. {rate}% VAT)"
                  ).replace("{rate}", String(MGMT_VAT_RATE))}
                </Label>
                <Input
                  id="invoice-edit-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  autoFocus
                />
                {(() => {
                  const consolidatedProps = Array.isArray(
                    (editFor?.fee_basis as { properties?: unknown[] } | null)
                      ?.properties
                  )
                    ? (editFor!.fee_basis as { properties: unknown[] })
                        .properties
                    : [];
                  if (consolidatedProps.length > 1) return null;
                  const parsed = parseFloat(editAmount);
                  if (!Number.isFinite(parsed) || parsed <= 0) return null;
                  const { netCents, vatCents, grossCents } = splitGrossVat(
                    Math.round(parsed * 100),
                    MGMT_VAT_RATE
                  );
                  return (
                    <p className="text-xs text-muted-foreground">
                      {t("admin.payments.net", "Net")}{" "}
                      {formatCents(netCents, "EUR")} +{" "}
                      {t("admin.payments.vat", "VAT")}{" "}
                      {formatCents(vatCents, "EUR")} ={" "}
                      {t("admin.payments.gross", "Gross")}{" "}
                      {formatCents(grossCents, "EUR")}
                    </p>
                  );
                })()}
              </div>
            )}
            {/* An offset invoice has no due date to set: the fee comes out of
                collected rent, and a due date is what would make the overdue
                derivation chase the owner for it. The generator and the reissue
                RPC both force NULL, so offering the field here would only let
                an admin type a value the write discards. */}
            {isOffsetInvoice(editFor ?? {}) ? (
              <div className="rounded-lg border bg-muted/40 px-3 py-2.5">
                <p className="text-xs font-medium">
                  {t(
                    "admin.payments.offsetSettledTitle",
                    "Settled against collected rent"
                  )}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {t(
                    "admin.payments.offsetSettledDescription",
                    "No due date — the owner is never asked to transfer this fee."
                  )}
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="invoice-edit-due">
                  {t("admin.payments.dueDate", "Due date")}
                </Label>
                <Input
                  id="invoice-edit-due"
                  type="date"
                  value={editDue}
                  onChange={(e) => setEditDue(e.target.value)}
                />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setEditFor(null);
                setEditThen(null);
              }}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              onClick={submitEdit}
              disabled={
                (!editAmount.trim() &&
                  !(editFor && isOffsetPropertyForecast(editFor))) ||
                createInvoice.isPending ||
                generatePropertyMonth.isPending ||
                editInvoice.isPending
              }
            >
              {(createInvoice.isPending ||
                generatePropertyMonth.isPending ||
                editInvoice.isPending) && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              )}
              {!editFor?.__projected
                ? t("common.save", "Save")
                : editThen === "proof"
                  ? t(
                      "admin.payments.createAndUploadProof",
                      "Create & upload proof"
                    )
                  : editThen === "note"
                    ? t("admin.payments.createAndAddNote", "Create & add note")
                    : t("admin.payments.createInvoiceTitle", "Create invoice")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  return { renderActions, overlays };
}
